import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { draw } from 'patternomaly'
import { settings } from '../modules/charts'

// Distinct fills so series are not distinguished by color alone (WCAG / Chart.js docs).
const BAR_PATTERNS = [
    'diagonal',
    'dot',
    'line',
    'cross',
    'diagonal-right-left',
    'zigzag',
    'line-vertical',
    'triangle',
    'diamond',
    'plus',
]

export default (chart) => {
    const chartContainer = chart.querySelector('.chart-container')
    if (!chartContainer) return

    let data = {}

    try {
        const chartData = JSON.parse(chart.querySelector('.chart-data')?.textContent || '{}')
        data = chartData
    } catch (e) {
        const warningMessage = chart.querySelector('.warning')
        warningMessage.classList.remove('hidden')
        return
    }

    const usePatterns = (data.bar_data?.length ?? 0) > 1

    const barData = data.bar_data.map((set, index) => {
        const color = set.backgroundColor
        const pattern = BAR_PATTERNS[index % BAR_PATTERNS.length]

        return {
            label: set.label,
            data: set.data,
            borderColor: set.borderColor ?? color,
            backgroundColor: usePatterns ? draw(pattern, color) : color,
            borderWidth: 1,
            type: 'bar',
        }
    })

    function toFiniteNumber(value) {
        const number = Number(value)
        return Number.isFinite(number) ? number : null
    }

    function getHighestDataValue() {
        if (!barData.length) return []
        const numPoints = Math.max(...barData.map(item => item.data.length))
        return Array.from({ length: numPoints }, (_, i) => {
            const values = barData
                .map(item => toFiniteNumber(item.data[i]))
                .filter(value => value !== null)

            return values.length ? Math.max(...values) : null
        })
    }

    const highs = getHighestDataValue().filter(value => value !== null)
    const highestValue = highs.length ? Math.max(...highs) : 0

    const circleValues = Array.isArray(data.circle_data?.data) ? data.circle_data.data : []
    const hasCircleValues = circleValues.some(value => value !== null && value !== undefined && value !== '')

    if (hasCircleValues) {
        const bubbleOffset = highestValue / 5

        barData.push({
            type: 'bubble',
            data: circleValues.map((value, index) => {
                const barHigh = getHighestDataValue()[index]
                const y = barHigh !== null ? barHigh + bubbleOffset : bubbleOffset

                return {
                    x: 0,
                    // make sure that the bubble is not on top of the bar
                    y,
                    r: value !== null && value !== undefined && value !== '' ? 40 : 0,
                    label: value !== null && value !== undefined && value !== '' ? value : 0,
                }
            }),
            backgroundColor: data.circle_data.backgroundColor,
            borderColor: '#000000',
            borderWidth: 3,
            label: '',
        })
    }

    new Chart(chartContainer, {
        data: {
            labels: data.labels,
            datasets: [...barData],
        },
        plugins: [ChartDataLabels],
        options: {
            barPercentage: 0.5,
            scales: {
                x: {
                    grid: {
                        display: data.vertical_lines_enabled ?? false,
                    },
                },
            },
            plugins: {
                // labels inside the bubbles
                datalabels: {
                    display: true,
                    align: 'center',
                    formatter: function (value, context) {
                        if (context.dataset.type === 'bubble') {
                            return value.label ? value.label.replaceAll(' ', '\n') : ''
                        }
                        return ''
                    },
                    color: data.circle_data?.textColor,
                    font: {
                        size: 18,
                        weight: 'bold',
                        family: 'DIN Pro, system-ui',
                    },
                    textAlign: 'center',
                },
                legend: {
                    ...settings.legend,
                },
            },
            tooltip: {
                enabled: false,
            },
        },
    })
}
